/**
 * Converts a DOM element to markdown by recursively walking child nodes.
 * Used as a fallback for docs pages that don't have raw markdown source.
 */
export function htmlElementToMarkdown(element: HTMLElement): string {
  const lines: string[] = [];
  walkNode(element, lines);
  // Collapse 3+ consecutive newlines into 2
  return lines.join('').replace(/\n{3,}/g, '\n\n').trim();
}

function walkNode(node: Node, lines: string[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || '';
    // Skip whitespace-only text nodes between block elements
    if (text.trim()) {
      lines.push(text);
    }
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  // Skip the copy button itself
  if (el.id === 'copy-markdown-btn') return;

  switch (tag) {
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6': {
      const level = parseInt(tag[1]);
      const prefix = '#'.repeat(level);
      lines.push(`\n\n${prefix} ${getTextContent(el)}\n\n`);
      return;
    }

    case 'p': {
      lines.push('\n\n');
      walkChildren(el, lines);
      lines.push('\n\n');
      return;
    }

    case 'pre': {
      const codeEl = el.querySelector('code');
      const langClass = codeEl?.className?.match(/language-(\w+)/);
      const lang = langClass ? langClass[1] : '';
      const code = codeEl?.textContent || el.textContent || '';
      lines.push(`\n\n\`\`\`${lang}\n${code.trim()}\n\`\`\`\n\n`);
      return;
    }

    case 'code': {
      // Inline code (not inside pre)
      if (el.parentElement?.tagName.toLowerCase() !== 'pre') {
        lines.push(`\`${el.textContent || ''}\``);
        return;
      }
      break;
    }

    case 'strong':
    case 'b':
      lines.push('**');
      walkChildren(el, lines);
      lines.push('**');
      return;

    case 'em':
    case 'i':
      lines.push('*');
      walkChildren(el, lines);
      lines.push('*');
      return;

    case 'a': {
      const href = el.getAttribute('href') || '';
      lines.push('[');
      walkChildren(el, lines);
      lines.push(`](${href})`);
      return;
    }

    case 'ul': {
      lines.push('\n');
      Array.from(el.children).forEach((li) => {
        lines.push('\n- ');
        walkChildren(li as HTMLElement, lines);
      });
      lines.push('\n\n');
      return;
    }

    case 'ol': {
      lines.push('\n');
      Array.from(el.children).forEach((li, i) => {
        lines.push(`\n${i + 1}. `);
        walkChildren(li as HTMLElement, lines);
      });
      lines.push('\n\n');
      return;
    }

    case 'br':
      lines.push('\n');
      return;

    case 'hr':
      lines.push('\n\n---\n\n');
      return;

    case 'blockquote':
      lines.push('\n\n> ');
      walkChildren(el, lines);
      lines.push('\n\n');
      return;

    case 'table': {
      convertTable(el, lines);
      return;
    }

    // Skip SVGs and images
    case 'svg':
    case 'img':
      return;
  }

  // Default: just walk children
  walkChildren(el, lines);
}

function walkChildren(el: HTMLElement, lines: string[]): void {
  el.childNodes.forEach((child) => walkNode(child, lines));
}

function getTextContent(el: HTMLElement): string {
  return (el.textContent || '').trim();
}

function convertTable(table: HTMLElement, lines: string[]): void {
  const rows = Array.from(table.querySelectorAll('tr'));
  if (rows.length === 0) return;

  lines.push('\n\n');
  rows.forEach((row, rowIdx) => {
    const cells = Array.from(row.querySelectorAll('th, td'));
    const cellTexts = cells.map((c) => (c.textContent || '').trim());
    lines.push(`| ${cellTexts.join(' | ')} |\n`);
    // Add separator after header row
    if (rowIdx === 0) {
      lines.push(`| ${cells.map(() => '---').join(' | ')} |\n`);
    }
  });
  lines.push('\n');
}
